import ControlLayout from '../components/ControlLayout.vue';

/*
Slot proxy
<template v-for="(s, name) in slots" v-slot:[name]="scope">
    <slot :name="name" v-bind="scope"></slot>
</template>
*/
export const ControlMixin = {
    inheritAttrs: false,
    components: { ControlLayout },
    inject: {
        panel: {
            name: 'panel',
            default: null
        }
    },
    beforeCreate() {
        this.$emitOriginal = this.$emit;
    },
    props: {
        /**
         * @model
         */
        value: {
            type: String,
            default: ''
        },
        /**
         * prop name to watch automatically without v-model / propChanged
         * @type {import('vue').PropOptions<string>}
         */
        prop: {
            type: String,
            default: ''
        },
        /**
         * Column size (12-fraction)
         *
         * @example 1-12, 2-12, ..., 12-12
         */
        colSize: {
            type: String,
            default: '12-12',
            validation(input) {
                const matches = input.match(/^(\d+)-(\d+)$/);
                if (!matches) {
                    return false;
                }
                matches.shift();
                return matches.every((frCount) => frCount >= 1 || frCount <= 12);
            }
        },
        /**
         * Whether the component is invalid
         */
        invalid: {
            type: Boolean,
            default: false
        },
        /**
         * Whether the component is disabled
         */
        disabled: {
            type: Boolean,
            default: false
        },
        /**
         * Whether the component is invalid
         */
        error: {
            type: [String, Object],
            default: null
        }
    },
    watch: {
        prop: {
            handler(propName) {
                if (this.hasPropName === false) {
                    return;
                }
                this.panel?.validatePropExist(this.prop);
            },
            immediate: true
        },
        hasPropName: {
            handler(hasPropName) {
                const autoPropEvents = ['input', 'change'];
                const emitChangeEvents = ['change'];
                
                if (hasPropName) {
                    this.$emit = function (eventName, value, ...rest) {
                        if (autoPropEvents.includes(eventName)) {
                            this.setPropAuto(value, emitChangeEvents.includes(eventName));
                        }
                        this.$emitOriginal.call(this, eventName, value, ...rest);
                    };
                    return;
                }
                this.$emit = this.$emitOriginal.bind(this);
            },
            immediate: true
        }
    },
    computed: {
        attrs() {
            const { colSize, value, disabled, ...attrs } = this.$attrs;
            return { ...attrs, disabled: this.disabled, value: this.valueResolved };
        },
        /**
         * @return {boolean}
         */
        hasPropName() {
            return this.prop !== '';
        },
        valueResolved() {
            // if 'prop' defined and no `v-model` or `:value` are defined
            if (this.hasPropName && 'value' in this.$options.propsData === false) {
                return this.panel?.getProp(this.prop);
            }
            return this.value;
        },
        listeners() {
            const { input, change, ...listeners } = { ...this.$listeners };
            return { ...listeners, input: this.onInput, change: this.onChange };
        },
        controlCl() {
            return { invalid: this.invalid || this.error, disabled: this.disabled };
        }
    },
    methods: {
        valueExtract: (x) => x,
        /**
         * @template T
         * @param {Event|T} eventOrValue
         * @return {T}
         */
        /**
         *
         * @param {Event|Array<any>|object|number|string|boolean|null} eventOrValue
         */
        onInput(eventOrValue) {
            /**
             * Input event
             *
             * @property {any} value
             */
            this.$emit('input', this.valueExtract(eventOrValue));
        },
        /**
         *
         * @param {Event|Array<any>|object|number|string|boolean|null} eventOrValue
         */
        onChange(eventOrValue) {
            this.$emit('change', this.valueExtract(eventOrValue));
        },
        /**
         *
         * @param {Array<any>|Record<string, any>|number|string|boolean|null} value
         * @param {boolean} emitChange
         */
        setPropAuto(value, emitChange = true) {
            this.panel?.setProp(this.prop, this.ensurePropValueType(value), emitChange);
        },
        /**
         *
         * @param {Array<any>|Record<string, any>|number|string|boolean|null} value
         * @return {number|*}
         */
        ensurePropValueType(value) {
            if (this.attrs.type === 'number') {
                return value === '' ? null : Number(value);
            }
            return value;
        }
    }
};
